import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ParentLayout from '../components/ParentLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft, RefreshCw, AlertCircle, CheckCircle2,
  MessageSquare, ShieldAlert, Phone, Moon, Sun,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDt = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const statusBadge = (status) => {
  const s = (status || '').toLowerCase();
  if (s === 'delivered' || s === 'sent' || s === 'accepted' || s === 'queued') {
    return <Badge className="bg-green-100 text-green-800 border-green-300">{status}</Badge>;
  }
  if (s === 'failed' || s === 'undelivered') {
    return <Badge className="bg-red-100 text-red-800 border-red-300">{status}</Badge>;
  }
  return <Badge variant="outline">{status || '—'}</Badge>;
};

const AdminSmsStatusPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Phase 2 role matrix: super_admin / league_admin / treasurer may view.
  // Only super_admin may write compliance / manage config.
  const canView = ['super_admin', 'league_admin', 'treasurer'].includes(user?.role);
  const canManageCompliance = user?.role === 'super_admin';

  const [status, setStatus] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchAll = async () => {
    setLoading(true);
    setError('');
    try {
      const [s, a] = await Promise.all([
        axios.get(`${API_URL}/api/admin/sms/status`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/sms/recent-attempts?limit=20`, { withCredentials: true }),
      ]);
      setStatus(s.data);
      setAttempts(a.data?.attempts || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load SMS status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canView) return;
    fetchAll();
  }, [canView]);

  if (!canView) {
    return (
      <ParentLayout>
        <div className="max-w-3xl mx-auto px-4 py-10 text-center" data-testid="sms-status-unauthorized">
          <ShieldAlert className="w-12 h-12 mx-auto text-red mb-3" />
          <h1 className="font-display text-xl font-black text-navy uppercase mb-2">Super-admin only</h1>
          <p className="text-sm text-gray-600">SMS observability is restricted to super-admin accounts.</p>
        </div>
      </ParentLayout>
    );
  }

  const enabled = status?.enabled;
  const reason = status?.disabled_reason;
  const dailyCap = status?.daily_cap ?? 250;
  const todaySent = status?.today_sent ?? 0;
  const todayPct = dailyCap > 0 ? Math.min(100, Math.round((todaySent / dailyCap) * 100)) : 0;
  const qh = status?.quiet_hours || {};

  return (
    <ParentLayout>
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-4" data-testid="admin-sms-status-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/dashboard')} data-testid="sms-status-back">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div>
              <h1 className="font-display text-2xl font-black text-navy uppercase tracking-tight flex items-center">
                <MessageSquare className="w-6 h-6 mr-2 text-gold" /> SMS Status
              </h1>
              <p className="text-sm text-gray-600">
                Operational observability for Twilio SMS — read-only. Activation steps live in&nbsp;
                <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">/app/memory/SMS_ACTIVATION_RUNBOOK.md</code>.
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchAll}
            disabled={loading}
            data-testid="sms-status-refresh"
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center" data-testid="sms-status-error">
            <AlertCircle className="w-4 h-4 text-red-600 mr-2" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Enabled banner */}
        <Card data-testid="sms-status-enabled-card">
          <CardContent className="p-4">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div className="flex items-start">
                {enabled ? (
                  <CheckCircle2 className="w-6 h-6 text-green-600 mr-3 mt-0.5" />
                ) : (
                  <AlertCircle className="w-6 h-6 text-yellow-600 mr-3 mt-0.5" />
                )}
                <div>
                  <p className="font-semibold text-navy" data-testid="sms-status-enabled-label">
                    {enabled ? 'SMS is ENABLED' : 'SMS is DISABLED'}
                  </p>
                  {!enabled && (
                    <p className="text-xs text-gray-600 mt-0.5" data-testid="sms-status-reason">
                      Reason: <code className="bg-gray-100 px-1 py-0.5 rounded">{reason || 'unknown'}</code>
                    </p>
                  )}
                  {enabled && status?.from_number && (
                    <p className="text-xs text-gray-600 mt-0.5 flex items-center" data-testid="sms-status-from-number">
                      <Phone className="w-3 h-3 mr-1" /> Sending from <code className="bg-gray-100 px-1 py-0.5 rounded ml-1">{status.from_number}</code>
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-2 text-xs text-gray-600">
                {qh.currently_quiet ? (
                  <Badge className="bg-indigo-100 text-indigo-800 border-indigo-300" data-testid="sms-status-quiet-now">
                    <Moon className="w-3 h-3 mr-1" /> Currently quiet hours
                  </Badge>
                ) : (
                  <Badge className="bg-yellow-50 text-yellow-800 border-yellow-200" data-testid="sms-status-sending-window">
                    <Sun className="w-3 h-3 mr-1" /> In sending window
                  </Badge>
                )}
                <span>{qh.tz} · {String(qh.start_hour).padStart(2, '0')}:00 – {String(qh.end_hour).padStart(2, '0')}:00 quiet</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sender configuration + compliance (Phase 3) */}
        <Card data-testid="sms-sender-config-card">
          <CardContent className="p-4 space-y-3">
            <p className="font-semibold text-navy text-sm">Sender configuration</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <div>
                <p className="text-gray-500 uppercase tracking-wider">Messaging Service</p>
                <p className="font-mono text-navy" data-testid="sms-config-messaging-service">
                  {status?.messaging_service_configured
                    ? (status.messaging_service_sid_masked || 'configured')
                    : <span className="text-yellow-700">not set</span>}
                </p>
              </div>
              <div>
                <p className="text-gray-500 uppercase tracking-wider">Account SID</p>
                <p className="font-mono text-navy" data-testid="sms-config-account-sid">
                  {status?.account_sid_masked || <span className="text-yellow-700">not set</span>}
                </p>
              </div>
              <div>
                <p className="text-gray-500 uppercase tracking-wider">Status callback</p>
                <p className="text-navy" data-testid="sms-config-status-callback">
                  {status?.status_callback_configured
                    ? <span className="text-green-700">registered</span>
                    : <span className="text-yellow-700">not registered</span>}
                </p>
              </div>
              <div>
                <p className="text-gray-500 uppercase tracking-wider">Inbound webhook</p>
                <p className="text-navy" data-testid="sms-config-inbound-webhook">
                  {status?.inbound_webhook_configured
                    ? <span className="text-green-700">registered</span>
                    : <span className="text-yellow-700">not registered</span>}
                </p>
              </div>
            </div>
            <div className="border-t pt-3">
              <p className="font-semibold text-navy text-sm mb-2">Compliance</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs" data-testid="sms-compliance-block">
                <div>
                  <p className="text-gray-500 uppercase tracking-wider">Sender type</p>
                  <p className="text-navy">{status?.compliance?.sender_type || '—'}</p>
                </div>
                <div>
                  <p className="text-gray-500 uppercase tracking-wider">Toll-free status</p>
                  <p className="text-navy">{status?.compliance?.toll_free_status || '—'}</p>
                </div>
                <div>
                  <p className="text-gray-500 uppercase tracking-wider">A2P 10DLC</p>
                  <p className="text-navy">{status?.compliance?.a2p_10dlc_status || '—'}</p>
                </div>
                <div>
                  <p className="text-gray-500 uppercase tracking-wider">Last reviewed</p>
                  <p className="text-navy">{fmtDt(status?.compliance?.last_reviewed_at)}</p>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Tracking record only — this dashboard does <strong>not</strong> confirm approval by Twilio. Verify carrier status in the Twilio console before sending live production traffic.
              </p>
            </div>
            <div className="border-t pt-3 grid grid-cols-2 gap-3 text-xs">
              <div>
                <p className="text-gray-500 uppercase tracking-wider">Last inbound message</p>
                <p className="text-navy" data-testid="sms-last-inbound">{fmtDt(status?.last_inbound_at)}</p>
              </div>
              <div>
                <p className="text-gray-500 uppercase tracking-wider">Last delivery callback</p>
                <p className="text-navy" data-testid="sms-last-delivery">{fmtDt(status?.last_delivery_at)}</p>
              </div>
            </div>
            {!canManageCompliance && (
              <p className="text-xs text-gray-500 italic">Only super_admin can update the compliance record.</p>
            )}
          </CardContent>
        </Card>

        {/* Stats grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card data-testid="sms-stat-today">
            <CardContent className="p-3">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Sent today</p>
              <p className="text-2xl font-black text-navy">{todaySent} <span className="text-sm text-gray-500 font-normal">/ {dailyCap}</span></p>
              <div className="h-1.5 mt-2 rounded-full bg-gray-200 overflow-hidden">
                <div className={`h-full ${todayPct >= 90 ? 'bg-red' : 'bg-green-600'}`} style={{ width: `${todayPct}%` }} />
              </div>
            </CardContent>
          </Card>
          <Card data-testid="sms-stat-failures">
            <CardContent className="p-3">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Failures (24h)</p>
              <p className={`text-2xl font-black ${(status?.recent_failures_24h ?? 0) > 0 ? 'text-red' : 'text-navy'}`}>
                {status?.recent_failures_24h ?? 0}
              </p>
            </CardContent>
          </Card>
          <Card data-testid="sms-stat-optedin">
            <CardContent className="p-3">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Opted-in users</p>
              <p className="text-2xl font-black text-navy">{status?.opted_in_users ?? 0}</p>
            </CardContent>
          </Card>
          <Card data-testid="sms-stat-stops">
            <CardContent className="p-3">
              <p className="text-xs text-gray-500 uppercase tracking-wider">STOP replies (7d)</p>
              <p className={`text-2xl font-black ${(status?.recent_inbound_stops_7d ?? 0) > 0 ? 'text-yellow-700' : 'text-navy'}`}>
                {status?.recent_inbound_stops_7d ?? 0}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Recent attempts table */}
        <Card data-testid="sms-recent-attempts">
          <CardContent className="p-0">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <p className="font-display font-black text-navy uppercase text-sm tracking-wider">Recent attempts</p>
              <span className="text-xs text-gray-500">Last {attempts.length}</span>
            </div>
            {loading ? (
              <div className="p-4 space-y-2" data-testid="sms-recent-loading">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="h-10 bg-gray-100 animate-pulse rounded" />
                ))}
              </div>
            ) : attempts.length === 0 ? (
              <div className="px-4 py-10 text-center" data-testid="sms-recent-empty">
                <p className="text-sm text-gray-600">No SMS attempts recorded yet.</p>
                <p className="text-xs text-gray-500 mt-1">
                  Once SMS is enabled and a reminder is triggered, attempts will appear here.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                    <tr>
                      <th className="text-left px-4 py-2">Time</th>
                      <th className="text-left px-4 py-2">Recipient</th>
                      <th className="text-left px-4 py-2">Phone</th>
                      <th className="text-left px-4 py-2">Type</th>
                      <th className="text-left px-4 py-2">Status</th>
                      <th className="text-left px-4 py-2">Batch / Event</th>
                      <th className="text-left px-4 py-2">Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attempts.map((a) => (
                      <tr key={a.message_id} className="border-t border-gray-100" data-testid={`sms-attempt-${a.message_id}`}>
                        <td className="px-4 py-2 text-gray-700 whitespace-nowrap">{fmtDt(a.attempted_at)}</td>
                        <td className="px-4 py-2 text-gray-900">{a.recipient_name || a.user_id || '—'}</td>
                        <td className="px-4 py-2 text-gray-600 font-mono text-xs">{a.to_phone_masked || '—'}</td>
                        <td className="px-4 py-2 text-gray-600 text-xs">{a.message_type || '—'}</td>
                        <td className="px-4 py-2">{statusBadge(a.status)}</td>
                        <td className="px-4 py-2 text-gray-500 font-mono text-xs">
                          {a.batch_id || a.event_id || '—'}
                        </td>
                        <td className="px-4 py-2 text-red-700 text-xs">{a.error_code || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </ParentLayout>
  );
};

export default AdminSmsStatusPage;
